import React, { useState, useMemo, useEffect } from "react";
import { useBar } from "../../../../context/BarContext";
import { useUserRole } from "../../../../context/UserRoleContext";
import { validateTimeRange } from "../../../../utils/timeValidation";
import BarOwnerArtistCard from "../BarOwnerArtistCard";
import ManagementArtistProfileModal from "../management/ManagementArtistProfileModal";
import { useBarOwnerData } from "../../../../context/BarOwnerDataContext";

const FindCovers = ({ isMapsLoaded = false, associations = {} }) => {
  const { selectedBar } = useBar();
  const { userRole } = useUserRole();
  const { invalidateCache, fetchManagementArtists } = useBarOwnerData();
  const [searchQuery, setSearchQuery] = useState("");
  const [showDate, setShowDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [artistSource, setArtistSource] = useState("my-artists"); // "my-artists" or "nearby"
  const [mileRange, setMileRange] = useState("");
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);

  // Mile range options for nearby search
  const mileRanges = [
    { label: "5 miles", value: "5" },
    { label: "10 miles", value: "10" },
    { label: "25 miles", value: "25" },
    { label: "50 miles", value: "50" },
    { label: "100 miles", value: "100" },
    { label: "500 miles", value: "500" },
  ];

  // Clear artists when any field changes
  useEffect(() => {
    setArtists([]);
    setError(null);
    setHasSearched(false);
  }, [
    artistSource,
    mileRange,
    selectedBar,
    searchQuery,
    showDate,
    startTime,
    endTime,
  ]);

  // Handle search button click
  const handleSearch = async () => {
    // Validate required fields
    if (!showDate) {
      setError("Show Date is required");
      return;
    }
    if (!startTime) {
      setError("Start Time is required");
      return;
    }
    if (!endTime) {
      setError("End Time is required");
      return;
    }

    // Validate time range
    if (startTime && endTime) {
      const validation = validateTimeRange(startTime, endTime);
      if (!validation.isValid) {
        setError(validation.error);
        return;
      }
    }

    if (artistSource === "nearby") {
      if (!selectedBar) {
        setError("Please select a bar to search for nearby artists");
        return;
      }
      if (!selectedBar.latitude || !selectedBar.longitude) {
        setError("Selected bar does not have location coordinates");
        return;
      }
      if (!mileRange) {
        setError("Search Radius is required for nearby search");
        return;
      }
    }

    if (artistSource === "my-artists" && userRole === "bar_admin") {
      if (!selectedBar) {
        setError("Please select a bar to search your artists for that venue");
        return;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append("source", artistSource);

      if (artistSource === "nearby") {
        params.append("barId", selectedBar.bar_id);
        params.append("radiusMiles", mileRange);
        if (userRole === "bar_admin") {
          params.append("excludeBarId", String(selectedBar.bar_id));
        } else {
          params.append("excludeOwnerBars", "1");
        }
      }

      if (artistSource === "my-artists" && userRole === "bar_admin" && selectedBar) {
        params.append("barId", String(selectedBar.bar_id));
      }

      if (searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }

      // Add availability filters (all required)
      params.append("date", showDate);
      params.append("startTime", startTime);
      params.append("endTime", endTime);

      const response = await fetch(
        `/api/artists/covers/search?${params.toString()}`,
        {
          credentials: "include",
        }
      );

      const data = await response.json();

      if (data.success) {
        setArtists(data.artists || []);
        setError(null);
        setHasSearched(true);
      } else {
        setError(data.error || "Failed to fetch artists");
        setArtists([]);
        setHasSearched(true);
      }
    } catch (err) {
      console.error("Error fetching artists:", err);
      setError(err.message);
      setArtists([]);
      setHasSearched(true);
    } finally {
      setLoading(false);
    }
  };

  // Filter artists client-side by search query (if not already filtered by API)
  const filteredArtists = useMemo(() => {
    if (
      !searchQuery.trim() ||
      artistSource === "nearby" ||
      artistSource === "my-artists"
    ) {
      // API already handles search for my-artists and nearby
      return artists;
    }

    // Fallback client-side filtering if needed
    const query = searchQuery.toLowerCase().trim();
    return artists.filter(
      (artist) =>
        artist.stage_name?.toLowerCase().includes(query) ||
        artist.hometown?.toLowerCase().includes(query)
    );
  }, [artists, searchQuery, artistSource]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Clear results when search query changes
    setArtists([]);
    setHasSearched(false);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setShowDate("");
    setStartTime("");
    setEndTime("");
    setArtistSource("my-artists");
    setMileRange("");
    setArtists([]);
    setError(null);
    setHasSearched(false);
  };

  const hasActiveFilters =
    searchQuery.trim() ||
    showDate ||
    startTime ||
    endTime ||
    artistSource !== "my-artists" ||
    mileRange;

  // Format distance for display
  const formatDistance = (distanceMiles) => {
    if (!distanceMiles && distanceMiles !== 0) return "";
    const miles = parseFloat(distanceMiles);
    if (miles < 1) {
      return `${Math.round(miles * 10) / 10} mi`;
    }
    return `${Math.round(miles * 10) / 10} mi`;
  };

  const handleArtistClick = (artist) => {
    setSelectedArtist(artist);
  };

  const handleRefresh = () => {
    invalidateCache("managementArtists");
    fetchManagementArtists();
    if (artistSource === "nearby") handleSearch();
  };

  return (
    <div className="artists-content">
      {/* Show Details Section */}
      <div className="search-section">
        {/* Two-column layout for Show Details and Find Artists */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "2rem",
            marginBottom: "1.5rem",
          }}
        >
          {/* Show Details Section - Left */}
          <div className="show-details-section">
            <h3 className="section-title">Show Details</h3>
            <div className="show-details-grid">
              <div className="input-group">
                <label className="input-label">Show Date *</label>
                <input
                  type="date"
                  value={showDate}
                  onChange={(e) => {
                    setShowDate(e.target.value);
                    setArtists([]);
                  }}
                  className="date-input"
                  min={new Date().toISOString().split("T")[0]}
                  required
                />
              </div>
              <div className="input-group">
                <label className="input-label">Start Time *</label>
                <input
                  type="time"
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    setArtists([]);
                  }}
                  className="time-input"
                  required
                />
              </div>
              <div className="input-group">
                <label className="input-label">End Time *</label>
                <input
                  type="time"
                  value={endTime}
                  onChange={(e) => {
                    setEndTime(e.target.value);
                    setArtists([]);
                  }}
                  className="time-input"
                  required
                />
              </div>
            </div>
          </div>

          {/* Artist Source Section - Right */}
          <div className="artist-source-section">
            <h3 className="section-title">Find Artists</h3>
            <div className="artist-source-grid">
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="artistSource"
                    value="my-artists"
                    checked={artistSource === "my-artists"}
                    onChange={(e) => {
                      setArtistSource(e.target.value);
                      setArtists([]);
                    }}
                    className="radio-input"
                  />
                  <span className="radio-custom"></span>
                  My Artists
                </label>
                <p className="radio-description">
                  Browse artists across all your bars
                </p>
              </div>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="artistSource"
                    value="nearby"
                    checked={artistSource === "nearby"}
                    onChange={(e) => {
                      setArtistSource(e.target.value);
                      setArtists([]);
                    }}
                    className="radio-input"
                  />
                  <span className="radio-custom"></span>
                  Nearby
                </label>
                <p className="radio-description">
                  Find new artists in your area
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Mile Range Section (only show when Nearby is selected) */}
        {artistSource === "nearby" && (
          <div className="mile-range-section">
            <div className="filter-group">
              <label className="filter-label">Search Radius *</label>
              <select
                value={mileRange}
                onChange={(e) => {
                  setMileRange(e.target.value);
                  setArtists([]);
                }}
                className="filter-select"
                required
              >
                <option value="">Select radius...</option>
                {mileRanges.map((range) => (
                  <option key={range.value} value={range.value}>
                    {range.label}
                  </option>
                ))}
              </select>
            </div>
            {!selectedBar && (
              <div
                className="error-message"
                style={{ color: "#f87171", marginTop: "0.5rem" }}
              >
                Please select a bar to search for nearby artists
              </div>
            )}
          </div>
        )}

        {/* Search Bar - After Find Artists selector */}
        <div className="search-bar-container">
          <input
            type="text"
            placeholder="Search artists by name, genre, or location..."
            value={searchQuery}
            onChange={handleSearchChange}
            className="search-input"
          />
          <span className="search-icon">🔍</span>
        </div>

        {/* Search Button */}
        <div className="filter-actions" style={{ marginTop: "1rem" }}>
          <button
            onClick={handleSearch}
            className="clear-filters-btn"
            style={{
              backgroundColor: "#ff0033",
              color: "white",
              padding: "0.75rem 2rem",
              fontSize: "1rem",
              fontWeight: 600,
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#cc0029";
              e.target.style.boxShadow = "0 0 20px rgba(255, 0, 51, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#ff0033";
              e.target.style.boxShadow = "none";
            }}
          >
            Search Artists
          </button>
        </div>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <div className="filter-actions" style={{ marginTop: "0.5rem" }}>
            <button onClick={clearFilters} className="clear-filters-btn">
              Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div
          className="error-message"
          style={{
            padding: "1rem",
            background: "rgba(248, 113, 113, 0.1)",
            border: "1px solid rgba(248, 113, 113, 0.3)",
            borderRadius: "8px",
            marginBottom: "1rem",
            color: "#f87171",
          }}
        >
          {error}
        </div>
      )}

      {/* Results Count */}
      {artists.length > 0 || loading ? (
        <div className="results-info">
          <span className="results-count">
            {loading ? (
              "Loading..."
            ) : (
              <>
                {filteredArtists.length} cover artist
                {filteredArtists.length !== 1 ? "s" : ""} found
              </>
            )}
          </span>
        </div>
      ) : null}

      {/* Artists Grid */}
      <div className="bars-grid bars-grid--artists">
        {loading ? (
          <div className="no-results" style={{ gridColumn: "1 / -1" }}>
            <div className="no-results-icon">⏳</div>
            <h3>Loading artists...</h3>
          </div>
        ) : filteredArtists.length > 0 ? (
          filteredArtists.map((artist) => (
            <BarOwnerArtistCard
              key={artist.artist_id}
              artist={artist}
              onClick={() => handleArtistClick(artist)}
              hasPending={false}
            />
          ))
        ) : artists.length === 0 && !loading ? (
          <div className="no-results" style={{ gridColumn: "1 / -1" }}>
            <div className="no-results-icon">{hasSearched ? "🎵" : "🔍"}</div>
            <h3>
              {hasSearched
                ? "No cover artists found"
                : "Fill in the search fields and click 'Search Artists'"}
            </h3>
            <p>
              {hasSearched
                ? "Try adjusting your search criteria or filters"
                : hasActiveFilters
                ? "Click 'Search Artists' to find available cover artists"
                : "Please fill in all required fields and click 'Search Artists' to find available cover artists"}
            </p>
          </div>
        ) : null}
      </div>

      {selectedArtist && (
        <ManagementArtistProfileModal
          isOpen={!!selectedArtist}
          onClose={() => setSelectedArtist(null)}
          artist={selectedArtist}
          associations={
            artistSource === "my-artists"
              ? associations[selectedArtist.artist_id] || []
              : []
          }
          selectedBar={selectedBar}
          isMapsLoaded={isMapsLoaded}
          onRefresh={handleRefresh}
          manageBarSelection={
            userRole === "bar_admin" ? "context" : "all"
          }
          contextBarId={selectedBar?.bar_id}
        />
      )}
    </div>
  );
};

export default FindCovers;
